// 
// Ticker.js
// cenit-website
// 
// Created by Matías Martínez on 21/8/25
// 

class DecelerationRate {
  // UIScrollViewDecelerationRateNormal
  static normal = 0.998;
    
  // UIScrollViewDecelerationRateFast
  static fast = 0.99;
}

class TickerMode {
  // Automatically scrolling or resting (when prefers-reduced-motion):
  static auto = "auto";
  
  // User is dragging:
  static dragging = "dragging";
  
  // User has lifted and we're decelerating:
  static decelerating = "decelerating";
}

class TickerAnimator {
  
  constructor({
    // The target node:
    node,
    
    // Speed for automatic scrolling:
    speed = 40,
    
    // Direction of automatic scrolling:
    direction = 1,
    
    // The deceleration rate for user-interactive scrolling:
    decelerationRate = DecelerationRate.normal,
    
    // Determines if the animation starts right away:
    animatesImmediately = true,
    
    // Smoothing parameters for decelerating:
    smoothing = { tau: 0.18, aMax: 6000, jMax: 40000, snapEps: 1.2 }
  } = {}){
    this.node = node;
    this.speed = speed;
    this.direction = Math.sign(direction) || 1;
    this.decelerationRate = decelerationRate;
    this.blend = smoothing;
    
    // Sizes:
    this.contentSize = { width: 0, height: 0 };
    this.gap = 0;
    
    // The original nodes and clones:
    this.scrollingNodes = [ ...node.children ];
    this.repeatingNodes = [];

    // Unwrapped physics state:
    this.uOffset = 0;
    this.offset = 0;
    this.vx = 0;
    this.mode = TickerMode.auto;

    // Animation tokens:
    this._request = null;
    this._lastTimestamp = null;

    // Drag + velocity sampler:
    this._drag = { active: false, lastX: 0, lastT: 0, samples: [] };

    // Blend state:
    this._a = 0; // acceleration tracker for jerk-limited blending

    // Compositor + gesture hints:
    const { style } = node;
    style.willChange = "transform";
    style.userSelect = "none";
    style.touchAction = "pan-y";

    this._reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    
    // Add event handlers:
    window.addEventListener("resize", this.invalidateContainerSize.bind(this));

    this.node.addEventListener("pointerdown", this._onPointerDown.bind(this), { passive: false });
    window.addEventListener("pointermove", this._onPointerMove.bind(this), { passive: false });
    window.addEventListener("pointerup", this._onPointerUp.bind(this));
    window.addEventListener("pointercancel", this._onPointerUp.bind(this));
    
    // Init:
    const init = () => {
      this.invalidateContainerSize();
      
      if (animatesImmediately) {
        this.start();
      }
    };
    
    window.requestAnimationFrame(init);
  }

  layout(){
    this.invalidateContentSizeIfNeeded();
    
    const containerW = this.containerSize.width;
    const neededRepeats = Math.ceil(containerW / this.contentSize.width) + 1;
    const repeatCount = Math.max(neededRepeats, 2);

    if (this.repeatCount === repeatCount) {
      return;
    }

    this.repeatingNodes.forEach(n => n.parentNode && n.parentNode.removeChild(n));

    const fragment = document.createDocumentFragment();
    for (let i = 0; i < repeatCount; i++){
      this.scrollingNodes.map(n => n.cloneNode(true)).forEach(n => fragment.appendChild(n));
    }
    this.node.appendChild(fragment);

    this.repeatCount = repeatCount;
    this.repeatingNodes = [ ...fragment.childNodes ];
  }
  
  invalidateContentSizeIfNeeded(){
    if (this.contentSize.width <= 0) {
      this.invalidateContentSize();
    }
  }
  
  invalidateContentSize(){
    const { node } = this;
    this.contentSize = node.getBoundingClientRect();
    this.gap = parseFloat(getComputedStyle(node).rowGap);
  }

  invalidateContainerSize(){
    this.containerSize = this.node.parentNode.getBoundingClientRect();
    this.layout();
  }

  // Velocity sampler:
  
  _addSample(x){
    const sampleWindowDuration = 50;
    
    const now = Date.now();
    this._drag.samples.push({ x, time: now });
    const expiry = now - sampleWindowDuration;
    const q = this._drag.samples;
    while (q.length && q[0].time < expiry) q.shift();
  }

  _computeVelocityOffsetSpace(){
    const velocityWindowDuration = 1000;
    
    const expiry = Date.now() - velocityWindowDuration;
    const q = this._drag.samples;
    while (q.length && q[0].time < expiry) q.shift();
    if (q.length < 2) return 0;
    const first = q[0], last = q[q.length - 1];
    const dt = (last.time - first.time) / 1000;
    if (dt <= 0) return 0;
    const dxPointer = last.x - first.x;
    return -(dxPointer / dt);
  }

  // Gestures:
  
  _onPointerDown(e){
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    this.node.setPointerCapture?.(e.pointerId);

    this.mode = TickerMode.dragging;
    this.vx = 0;

    this._drag.active = true;
    this._drag.lastX = e.clientX;
    this._drag.lastT = performance.now();
    this._drag.samples.length = 0;
    this._addSample(e.clientX);

    if (!this._request) {
      this._lastTimestamp = null;
      this._request = requestAnimationFrame(this.frame.bind(this));
    }
  }

  _onPointerMove(e){
    if (!this._drag.active) {
      return;
    }

    const dx = e.clientX - this._drag.lastX;
    this.uOffset -= dx;
    this._addSample(e.clientX);

    this._drag.lastX = e.clientX;
    this._drag.lastT = performance.now();

    this._display();
  }

  _onPointerUp(_e){
    if (!this._drag.active) {
      return;
    }
    this._drag.active = false;

    let v = this._computeVelocityOffsetSpace();
    
    const maximumFlingVelocity = 2000;
    v = Math.max(-maximumFlingVelocity, Math.min(maximumFlingVelocity, v));
    this.vx = v;

    if (Math.abs(this.vx) > 1) {
      this.direction = this.vx >= 0 ? 1 : -1;
    }

    if (this._reducedMotion) {
      this.mode = TickerMode.auto;
      return;
    }

    this._a = 0; // reset accel for blend
    this.mode = TickerMode.decelerating;
  }

  // ----- RAF -----
  frame(ts){
    if (this._lastTimestamp == null) {
      this._lastTimestamp = ts;
    }
    
    const dt = Math.min(0.05, (ts - this._lastTimestamp) / 1000);
    this._lastTimestamp = ts;

    if (this.mode === TickerMode.auto && !this._reducedMotion) {
      this.uOffset += this.speed * dt * this.direction;

    } else if (this.mode === TickerMode.decelerating) {
      const vAuto = this.speed * this.direction;
      const { tau = 0.18, aMax = 6000, jMax = 40000, snapEps = 1.2 } = this.blend;

      // desired accel
      const aDes = (vAuto - this.vx) / Math.max(0.001, tau);
      // jerk limit
      const aDeltaMax = jMax * dt;
      const aNextUnclamped =
        Math.abs(aDes - this._a) > aDeltaMax
          ? this._a + Math.sign(aDes - this._a) * aDeltaMax
          : aDes;
      // clamp accel
      this._a = Math.max(-aMax, Math.min(aMax, aNextUnclamped));

      this.vx += this._a * dt;
      this.uOffset += this.vx * dt;

      if (Math.abs(this.vx - vAuto) < snapEps && Math.abs(this._a) < aMax * 0.05) {
        this.vx = vAuto;
        this._a = 0;
        this.mode = TickerMode.auto;
      }
    }

    this._display();
    this._request = requestAnimationFrame(this.frame.bind(this));
  }

  // 
  
  _display(){
    const w = this.contentSize.width + this.gap || 1;
    this.offset = ((this.uOffset % w) + w) % w;
    const x = -this.offset;
    this.node.style.transform = new DOMMatrix().translate(x, 0);
  }

  start(){
    if (this._request || this._reducedMotion) return;
    this._lastTimestamp = null;
    this._request = requestAnimationFrame(this.frame.bind(this));
  }

  stop(){
    if (this._request) cancelAnimationFrame(this._request);
    this._request = null;
  }
}

export { TickerAnimator, DecelerationRate };
export default TickerAnimator;
